import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import { delegate } from 'tippy.js';
import unified from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import ghSchema from 'hast-util-sanitize/lib/github.json';
import visit from 'unist-util-visit';
import selectFiles from 'select-files';
import wordCount from 'word-count';
import factory from 'codemirror-ssr';
import usePlaceholder from 'codemirror-ssr/addon/display/placeholder';
import useOverlay from 'codemirror-ssr/addon/mode/overlay.js';
import useXml from 'codemirror-ssr/mode/xml/xml';
import useMarkdown from 'codemirror-ssr/mode/markdown/markdown';
import useGfm from 'codemirror-ssr/mode/gfm/gfm';
import useYaml from 'codemirror-ssr/mode/yaml/yaml';
import useYamlFrontmatter from 'codemirror-ssr/mode/yaml-frontmatter/yaml-frontmatter';

function noop() { }
function run(fn) {
    return fn();
}
function blank_object() {
    return Object.create(null);
}
function run_all(fns) {
    fns.forEach(run);
}
function is_function(thing) {
    return typeof thing === 'function';
}
function safe_not_equal(a, b) {
    return a != a ? b == b : a !== b || ((a && typeof a === 'object') || typeof a === 'function');
}
function not_equal(a, b) {
    return a != a ? b == b : a !== b;
}
function is_empty(obj) {
    return Object.keys(obj).length === 0;
}
function append(target, node) {
    target.appendChild(node);
}
function insert(target, node, anchor) {
    target.insertBefore(node, anchor || null);
}
function detach(node) {
    node.parentNode.removeChild(node);
}
function destroy_each(iterations, detaching) {
    for (let i = 0; i < iterations.length; i += 1) {
        if (iterations[i])
            iterations[i].d(detaching);
    }
}
function element(name) {
    return document.createElement(name);
}
function text(data) {
    return document.createTextNode(data);
}
function empty() {
    return text('');
}
function listen(node, event, handler, options) {
    node.addEventListener(event, handler, options);
    return () => node.removeEventListener(event, handler, options);
}
function attr(node, attribute, value) {
    if (value == null)
        node.removeAttribute(attribute);
    else if (node.getAttribute(attribute) !== value)
        node.setAttribute(attribute, value);
}
function children(element) {
    return Array.from(element.childNodes);
}
function set_data(text, data) {
    data = '' + data;
    if (text.wholeText !== data)
        text.data = data;
}
function toggle_class(element, name, toggle) {
    element.classList[toggle ? 'add' : 'remove'](name);
}
function custom_event(type, detail, bubbles = false) {
    const e = document.createEvent('CustomEvent');
    e.initCustomEvent(type, bubbles, false, detail);
    return e;
}

let current_component;
function set_current_component(component) {
    current_component = component;
}
function get_current_component() {
    if (!current_component)
        throw new Error('Function called outside component initialization');
    return current_component;
}
function onMount(fn) {
    get_current_component().$$.on_mount.push(fn);
}
function afterUpdate(fn) {
    get_current_component().$$.after_update.push(fn);
}
function onDestroy(fn) {
    get_current_component().$$.on_destroy.push(fn);
}
function createEventDispatcher() {
    const component = get_current_component();
    return (type, detail) => {
        const callbacks = component.$$.callbacks[type];
        if (callbacks) {
            // TODO are there situations where events could be dispatched
            // in a server (non-DOM) environment?
            const event = custom_event(type, detail);
            callbacks.slice().forEach(fn => {
                fn.call(component, event);
            });
        }
    };
}

const dirty_components = [];
const binding_callbacks = [];
const render_callbacks = [];
const flush_callbacks = [];
const resolved_promise = Promise.resolve();
let update_scheduled = false;
function schedule_update() {
    if (!update_scheduled) {
        update_scheduled = true;
        resolved_promise.then(flush);
    }
}
function tick() {
    schedule_update();
    return resolved_promise;
}
function add_render_callback(fn) {
    render_callbacks.push(fn);
}
let flushing = false;
const seen_callbacks = new Set();
function flush() {
    if (flushing)
        return;
    flushing = true;
    do {
        // first, call beforeUpdate functions
        // and update components
        for (let i = 0; i < dirty_components.length; i += 1) {
            const component = dirty_components[i];
            set_current_component(component);
            update(component.$$);
        }
        set_current_component(null);
        dirty_components.length = 0;
        while (binding_callbacks.length)
            binding_callbacks.pop()();
        // then, once components are updated, call
        // afterUpdate functions. This may cause
        // subsequent updates...
        for (let i = 0; i < render_callbacks.length; i += 1) {
            const callback = render_callbacks[i];
            if (!seen_callbacks.has(callback)) {
                // ...so guard against infinite loops
                seen_callbacks.add(callback);
                callback();
            }
        }
        render_callbacks.length = 0;
    } while (dirty_components.length);
    while (flush_callbacks.length) {
        flush_callbacks.pop()();
    }
    update_scheduled = false;
    flushing = false;
    seen_callbacks.clear();
}
function update($$) {
    if ($$.fragment !== null) {
        $$.update();
        run_all($$.before_update);
        const dirty = $$.dirty;
        $$.dirty = [-1];
        $$.fragment && $$.fragment.p($$.ctx, dirty);
        $$.after_update.forEach(add_render_callback);
    }
}
const outroing = new Set();
let outros;
function group_outros() {
    outros = {
        r: 0,
        c: [],
        p: outros // parent group
    };
}
function check_outros() {
    if (!outros.r) {
        run_all(outros.c);
    }
    outros = outros.p;
}
function transition_in(block, local) {
    if (block && block.i) {
        outroing.delete(block);
        block.i(local);
    }
}
function transition_out(block, local, detach, callback) {
    if (block && block.o) {
        if (outroing.has(block))
            return;
        outroing.add(block);
        outros.c.push(() => {
            outroing.delete(block);
            if (callback) {
                if (detach)
                    block.d(1);
                callback();
            }
        });
        block.o(local);
    }
}
function create_component(block) {
    block && block.c();
}
function mount_component(component, target, anchor, customElement) {
    const { fragment, on_mount, on_destroy, after_update } = component.$$;
    fragment && fragment.m(target, anchor);
    if (!customElement) {
        // onMount happens before the initial afterUpdate
        add_render_callback(() => {
            const new_on_destroy = on_mount.map(run).filter(is_function);
            if (on_destroy) {
                on_destroy.push(...new_on_destroy);
            }
            else {
                // Edge case - component was destroyed immediately,
                // most likely as a result of a binding initialising
                run_all(new_on_destroy);
            }
            component.$$.on_mount = [];
        });
    }
    after_update.forEach(add_render_callback);
}
function destroy_component(component, detaching) {
    const $$ = component.$$;
    if ($$.fragment !== null) {
        run_all($$.on_destroy);
        $$.fragment && $$.fragment.d(detaching);
        // TODO null out other refs, including component.$$ (but need to
        // preserve final state?)
        $$.on_destroy = $$.fragment = null;
        $$.ctx = [];
    }
}
function make_dirty(component, i) {
    if (component.$$.dirty[0] === -1) {
        dirty_components.push(component);
        schedule_update();
        component.$$.dirty.fill(0);
    }
    component.$$.dirty[(i / 31) | 0] |= (1 << (i % 31));
}
function init(component, options, instance, create_fragment, not_equal, props, append_styles, dirty = [-1]) {
    const parent_component = current_component;
    set_current_component(component);
    const $$ = component.$$ = {
        fragment: null,
        ctx: null,
        // state
        props,
        update: noop,
        not_equal,
        bound: blank_object(),
        // lifecycle
        on_mount: [],
        on_destroy: [],
        on_disconnect: [],
        before_update: [],
        after_update: [],
        context: new Map(options.context || (parent_component ? parent_component.$$.context : [])),
        // everything else
        callbacks: blank_object(),
        dirty,
        skip_bound: false,
        root: options.target || parent_component.$$.root
    };
    append_styles && append_styles($$.root);
    let ready = false;
    $$.ctx = instance
        ? instance(component, options.props || {}, (i, ret, ...rest) => {
            const value = rest.length ? rest[0] : ret;
            if ($$.ctx && not_equal($$.ctx[i], $$.ctx[i] = value)) {
                if (!$$.skip_bound && $$.bound[i])
                    $$.bound[i](value);
                if (ready)
                    make_dirty(component, i);
            }
            return ret;
        })
        : [];
    $$.update();
    ready = true;
    run_all($$.before_update);
    // `false` as a special case of no DOM component
    $$.fragment = create_fragment ? create_fragment($$.ctx) : false;
    if (options.target) {
        if (options.hydrate) {
            const nodes = children(options.target);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.l(nodes);
            nodes.forEach(detach);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            $$.fragment && $$.fragment.c();
        }
        if (options.intro)
            transition_in(component.$$.fragment);
        mount_component(component, options.target, options.anchor, options.customElement);
        flush();
    }
    set_current_component(parent_component);
}
/**
 * Base class for Svelte components. Used when dev=false.
 */
class SvelteComponent {
    $destroy() {
        destroy_component(this, 1);
        this.$destroy = noop;
    }
    $on(type, callback) {
        const callbacks = (this.$$.callbacks[type] || (this.$$.callbacks[type] = []));
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index !== -1)
                callbacks.splice(index, 1);
        };
    }
    $set($$props) {
        if (this.$$set && !is_empty($$props)) {
            this.$$.skip_bound = true;
            this.$$set($$props);
            this.$$.skip_bound = false;
        }
    }
}

// DO NOT EDIT, generated by scripts/icon.js
const icons = {
    heading: '<svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 1v13.33M12.997 1v13.33M3 7.665h9.998" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h1: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M25 8v32M6 24h19M34.226 24 39 19.017V40" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h2: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M24 8v32M7 24h16M32 25c0-3.167 2.667-5 5-5s5 1.833 5 5c0 5.7-10 9.933-10 15h10" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h3: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M24 8v32M7 24h16M32 20h10l-7 9c4 0 7 2 7 6s-3 5-5 5c-2.381 0-4-1-5-2.1" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h4: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M24 8v32M7 24h16M39.977 40V20L31 32.997v2.023h12" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h5: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M24 8v32M7 24h16M40 21.01h-8v7.024C32 28 34 27 37 27s4 2.534 4 6.5-1 6.5-5 6.5c-3 0-4-2-4-3.992" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    h6: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8v32M24 8v32M7 24h16" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M36.5 40a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z" stroke="currentColor" stroke-width="4"/><path d="M41.596 24.74C40.778 22.545 38.804 21 36.5 21c-3.038 0-5.5 2.686-5.5 6v7" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    bold: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path clip-rule="evenodd" d="M24 24c5.506 0 9.969-4.477 9.969-10S29.506 4 24 4H11v20h13zM28.031 44C33.537 44 38 39.523 38 34s-4.463-10-9.969-10H11v20h17.031z" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    italic: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6h16M12 42h16M29 5.952 19 42" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    quote: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M18.853 9.116c-7.53 4.836-11.714 10.465-12.55 16.887C5 36 13.94 40.893 18.47 36.497 23 32.1 20.285 26.52 17.005 24.994c-3.28-1.525-5.286-.994-4.936-3.032.35-2.039 5.016-7.69 9.116-10.323a.749.749 0 0 0 .114-1.02L20.285 9.3c-.44-.572-.862-.55-1.432-.185zM38.679 9.116c-7.53 4.836-11.714 10.465-12.55 16.887-1.303 9.997 7.637 14.89 12.167 10.494 4.53-4.397 1.815-9.977-1.466-11.503-3.28-1.525-5.286-.994-4.936-3.032.35-2.039 5.017-7.69 9.117-10.323a.749.749 0 0 0 .113-1.02L40.11 9.3c-.44-.572-.862-.55-1.431-.185z" fill="currentColor"/></svg>',
    link: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m26.24 16.373-9.14-9.14c-2.661-2.661-7.035-2.603-9.769.131-2.733 2.734-2.792 7.107-.13 9.768l7.935 7.936M32.903 23.003l7.935 7.936c2.661 2.66 2.603 7.034-.13 9.768-2.735 2.734-7.108 2.792-9.77.131l-9.14-9.14" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M26.11 26.142c2.733-2.734 2.791-7.108.13-9.769M21.799 21.799c-2.734 2.733-2.793 7.107-.131 9.768" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    image: '<svg width="1em" height="1em" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" fill-opacity=".01" fill-rule="evenodd" d="M0 0h48v48H0z"/><g transform="translate(5 8)" stroke-width="4" stroke-linejoin="round" stroke="currentColor" fill="none"><path d="M2 0h34a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2z" stroke-linecap="round"/><circle stroke-linecap="round" cx="9.5" cy="8.5" r="1.5"/><path d="m10 16 5 4 6-7 17 13v4a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-4l10-10z"/></g></svg>',
    code: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 13 4 25.432 16 37M32 13l12 12.432L32 37" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="m28 4-7 40" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>',
    codeBlock: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 4c-2 0-5 1-5 5v9c0 3-5 5-5 5s5 2 5 5v11c0 4 3 5 5 5M32 4c2 0 5 1 5 5v9c0 3 5 5 5 5s-5 2-5 5v11c0 4-3 5-5 5" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    ol: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 4v9M12 13H6M12 27H6M6 20s3-3 5 0-5 7-5 7M6 34.5s2-3 5-1 0 4.5 0 4.5 3 2.5 0 4.5-5-1-5-1M11 38H9M9 4 6 6M21 24h22M21 38h22M21 10h22" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    ul: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 42a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM9 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM9 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M21 24h22M21 38h22M21 10h22" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    hr: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 24h38M21 38h6M37 38h6M21 10h6M5 38h6M5 10h6M37 10h6" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    source: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" fill-opacity=".01" d="M0 0h48v48H0z"/><path d="M29.344 30.477c2.404-.5 4.585-1.366 6.28-2.638C38.52 25.668 40 22.314 40 19c0-2.325-.881-4.495-2.407-6.333-.85-1.024 1.636-8.667-.573-7.638-2.21 1.03-5.45 3.308-7.147 2.805A20.712 20.712 0 0 0 24 7c-1.8 0-3.532.223-5.147.635C16.505 8.232 14.259 6 12 5.029c-2.26-.97-1.026 6.934-1.697 7.766C8.84 14.605 8 16.729 8 19c0 3.314 1.79 6.668 4.686 8.84 1.93 1.446 4.348 2.368 7.054 2.822M19.74 30.662c-1.158 1.275-1.738 2.486-1.738 3.633v8.716M29.344 30.477c1.098 1.44 1.647 2.734 1.647 3.88v8.654M6 31.216c.899.11 1.566.523 2 1.24.652 1.074 3.074 5.062 5.825 5.062h4.177" stroke="currentColor" stroke-width="4" stroke-linecap="round"/></svg>',
    fullscreenOn: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M33 6h9v9M42 33v9h-9M15 42H6v-9M6 15V6h9" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    fullscreenOff: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M33 6v9h9M15 6v9H6M15 42v-9H6M33 42v-9h8.9" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    help: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M39 6H9a3 3 0 0 0-3 3v30a3 3 0 0 0 3 3h30a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3z" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M24 28.625v-4a6 6 0 1 0-6-6" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path fill-rule="evenodd" clip-rule="evenodd" d="M24 37.625a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" fill="currentColor"/></svg>',
    toc: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M39 6H9a3 3 0 0 0-3 3v30a3 3 0 0 0 3 3h30a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3z" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M26 24H14M34 15H14M32 33H14" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    close: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" fill-opacity=".01" d="M0 0h48v48H0z"/><path d="m8 8 32 32M8 40 40 8" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    left: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6" y="6" width="28" height="36" rx="2" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M42 6v36" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    right: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="14" y="6" width="28" height="36" rx="2" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 6v36" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    more: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" fill-opacity=".01" d="M0 0h48v48H0z"/><circle cx="12" cy="24" r="3" fill="currentColor"/><circle cx="24" cy="24" r="3" fill="currentColor"/><circle cx="36" cy="24" r="3" fill="currentColor"/></svg>',
    keyboard: '<svg width="1em" height="1em" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M42 7H6a2 2 0 0 0-2 2v28a2 2 0 0 0 2 2h36a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" stroke="currentColor" stroke-width="4" stroke-linejoin="round"/><path d="M12 19h2M21 19h2M29 19h7M12 28h24" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
};

/* packages/bytemd/lib/toolbar.svelte generated by Svelte v3.44.2 */

function get_each_context$2(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[21] = list[i];
	child_ctx[23] = i;
	return child_ctx;
}

function get_each_context_1$1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[21] = list[i];
	child_ctx[23] = i;
	return child_ctx;
}

// (259:46) {:else}
function create_else_block(ctx) {
	let div0;
	let t0_value = /*locale*/ ctx[2].write + "";
	let t0;
	let div1;
	let t1_value = /*locale*/ ctx[2].preview + "";
	let t1;
	let mounted;
	let dispose;

	return {
		c() {
			div0 = element("div");
			t0 = text(t0_value);
			div1 = element("div");
			t1 = text(t1_value);
			attr(div0, "class", "bytemd-toolbar-tab");
			toggle_class(div0, "bytemd-toolbar-tab-active", /*activeTab*/ ctx[1] !== 'preview');
			attr(div1, "class", "bytemd-toolbar-tab");
			toggle_class(div1, "bytemd-toolbar-tab-active", /*activeTab*/ ctx[1] === 'preview');
		},
		m(target, anchor) {
			insert(target, div0, anchor);
			append(div0, t0);
			insert(target, div1, anchor);
			append(div1, t1);

			if (!mounted) {
				dispose = [
					listen(div0, "click", /*click_handler*/ ctx[15]),
					listen(div1, "click", /*click_handler_1*/ ctx[16])
				];

				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty & /*locale*/ 4 && t0_value !== (t0_value = /*locale*/ ctx[2].write + "")) set_data(t0, t0_value);

			if (dirty & /*activeTab*/ 2) {
				toggle_class(div0, "bytemd-toolbar-tab-active", /*activeTab*/ ctx[1] !== 'preview');
			}

			if (dirty & /*locale*/ 4 && t1_value !== (t1_value = /*locale*/ ctx[2].preview + "")) set_data(t1, t1_value);

			if (dirty & /*activeTab*/ 2) {
				toggle_class(div1, "bytemd-toolbar-tab-active", /*activeTab*/ ctx[1] === 'preview');
			}
		},
		d(detaching) {
			if (detaching) detach(div0);
			if (detaching) detach(div1);
			mounted = false;
			run_all(dispose);
		}
	};
}

// (256:113) {#if split}
function create_if_block_1$2(ctx) {
	let each_1_anchor;
	let each_value_1 = /*actions*/ ctx[3];
	let each_blocks = [];

	for (let i = 0; i < each_value_1.length; i += 1) {
		each_blocks[i] = create_each_block_1$1(get_each_context_1$1(ctx, each_value_1, i));
	}

	return {
		c() {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			each_1_anchor = empty();
		},
		m(target, anchor) {
			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(target, anchor);
			}

			insert(target, each_1_anchor, anchor);
		},
		p(ctx, dirty) {
			if (dirty & /*tippyClass, actions*/ 8) {
				each_value_1 = /*actions*/ ctx[3];
				let i;

				for (i = 0; i < each_value_1.length; i += 1) {
					const child_ctx = get_each_context_1$1(ctx, each_value_1, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block_1$1(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(each_1_anchor.parentNode, each_1_anchor);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value_1.length;
			}
		},
		d(detaching) {
			destroy_each(each_blocks, detaching);
			if (detaching) detach(each_1_anchor);
		}
	};
}

// (256:154) {#if item.handler}
function create_if_block_2(ctx) {
	let div;
	let raw_value = /*item*/ ctx[21].icon + "";
	let div_class_value;
	let div_bytemd_tippy_path_value;

	return {
		c() {
			div = element("div");
			attr(div, "class", div_class_value = ['bytemd-toolbar-icon', tippyClass].join(' '));
			attr(div, "bytemd-tippy-path", div_bytemd_tippy_path_value = /*index*/ ctx[23]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty & /*actions*/ 8 && raw_value !== (raw_value = /*item*/ ctx[21].icon + "")) div.innerHTML = raw_value;		},
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

// (256:124) {#each actions as item, index}
function create_each_block_1$1(ctx) {
	let if_block_anchor;
	let if_block = /*item*/ ctx[21].handler && create_if_block_2(ctx);

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
		},
		p(ctx, dirty) {
			if (/*item*/ ctx[21].handler) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_2(ctx);
					if_block.c();
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (267:201) {#if !item.hidden}
function create_if_block$3(ctx) {
	let div;
	let raw_value = /*item*/ ctx[21].icon + "";
	let div_class_value;
	let div_bytemd_tippy_path_value;

	return {
		c() {
			div = element("div");
			attr(div, "class", div_class_value = ['bytemd-toolbar-icon', tippyClass, tippyClassRight].join(' '));
			attr(div, "bytemd-tippy-path", div_bytemd_tippy_path_value = /*index*/ ctx[23]);
			toggle_class(div, "bytemd-toolbar-icon-active", /*item*/ ctx[21].active);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = raw_value;
		},
		p(ctx, dirty) {
			if (dirty & /*rightActions*/ 32 && raw_value !== (raw_value = /*item*/ ctx[21].icon + "")) div.innerHTML = raw_value;
			if (dirty & /*rightActions*/ 32) {
				toggle_class(div, "bytemd-toolbar-icon-active", /*item*/ ctx[21].active);
			}
		},
		d(detaching) {
			if (detaching) detach(div);
		}
	};
}

// (267:166) {#each rightActions as item, index}
function create_each_block$2(ctx) {
	let if_block_anchor;
	let if_block = !/*item*/ ctx[21].hidden && create_if_block$3(ctx);

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
		},
		p(ctx, dirty) {
			if (!/*item*/ ctx[21].hidden) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block$3(ctx);
					if_block.c();
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

function create_fragment$5(ctx) {
	let div2;
	let div0;
	let div1;
	let mounted;
	let dispose;

	function select_block_type(ctx, dirty) {
		if (/*split*/ ctx[0]) return create_if_block_1$2;
		return create_else_block;
	}

	let current_block_type = select_block_type(ctx);
	let if_block = current_block_type(ctx);
	let each_value = /*rightActions*/ ctx[5];
	let each_blocks = [];

	for (let i = 0; i < each_value.length; i += 1) {
		each_blocks[i] = create_each_block$2(get_each_context$2(ctx, each_value, i));
	}

	return {
		c() {
			div2 = element("div");
			div0 = element("div");
			if_block.c();
			div1 = element("div");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(div0, "class", "bytemd-toolbar-left");
			attr(div1, "class", "bytemd-toolbar-right");
			attr(div2, "class", "bytemd-toolbar");
		},
		m(target, anchor) {
			insert(target, div2, anchor);
			append(div2, div0);
			if_block.m(div0, null);
			append(div2, div1);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(div1, null);
			}

			/*div2_binding*/ ctx[17](div2);

			if (!mounted) {
				dispose = listen(div2, "click", /*handleClick*/ ctx[7]);
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (current_block_type === (current_block_type = select_block_type(ctx)) && if_block) {
				if_block.p(ctx, dirty);
			} else {
				if_block.d(1);
				if_block = current_block_type(ctx);

				if (if_block) {
					if_block.c();
					if_block.m(div0, null);
				}
			}

			if (dirty & /*tippyClass, tippyClassRight, rightActions*/ 32) {
				each_value = /*rightActions*/ ctx[5];
				let i;

				for (i = 0; i < each_value.length; i += 1) {
					const child_ctx = get_each_context$2(ctx, each_value, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block$2(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(div1, null);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value.length;
			}
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div2);
			if_block.d();
			destroy_each(each_blocks, detaching);
			/*div2_binding*/ ctx[17](null);
			mounted = false;
			dispose();
		}
	};
}

const tippyClass = 'bytemd-tippy';
const tippyClassRight = 'bytemd-tippy-right';
const tippyPathKey = 'bytemd-tippy-path';

function instance$5($$self, $$props, $$invalidate) {
	let tocActive;
	let helpActive;
	let writeActive;
	let previewActive;
	let rightActions;
	const dispatch = createEventDispatcher();
	let toolbar;
	let { context } = $$props;
	let { split } = $$props;
	let { activeTab } = $$props;
	let { fullscreen } = $$props;
	let { sidebar } = $$props;
	let { locale } = $$props;
	let { actions } = $$props;

	function getPayloadFromElement(e) {
		var _a, _b;

		const paths = (_b = (_a = e.getAttribute(tippyPathKey)) === null || _a === void 0
		? void 0
		: _a.split('-')) === null || _b === void 0
		? void 0
		: _b.map(x => parseInt(x, 10));

		if (!paths) return;

		// if (!paths) {
		//   return {
		//     paths: [],
		//     item: {
		//       title: 'test',
		//       handler: actions,
		//     },
		//   };
		// }
		let item = {
			title: '',
			handler: {
				type: 'dropdown',
				actions: e.classList.contains(tippyClassRight)
				? rightActions
				: actions
			}
		};

		paths === null || paths === void 0
		? void 0
		: paths.forEach(index => {
				var _a;

				if (((_a = item.handler) === null || _a === void 0
				? void 0
				: _a.type) === 'dropdown') {
					item = item.handler.actions[index];
				}
			});

		return { paths, item };
	}

	let delegateInstance;

	function init() {
		delegateInstance = delegate(toolbar, {
			target: `.${tippyClass}`,
			onCreate({ setProps, reference }) {
				const payload = getPayloadFromElement(reference);
				if (!payload) return;
				const { item, paths } = payload;
				const { handler } = item;
				if (!handler) return;

				if (handler.type === 'action') {
					setProps({
						content: item.title,
						onHidden(ins) {
							ins.destroy();
						}
					});
				} else if (handler.type === 'dropdown') {
					// dropdown
					const dropdown = document.createElement('div');

					dropdown.classList.add('bytemd-dropdown');

					if (item.title) {
						const dropdownTitle = document.createElement('div');
						dropdownTitle.classList.add('bytemd-dropdown-title');
						dropdownTitle.appendChild(document.createTextNode(item.title));
						dropdown.appendChild(dropdownTitle);
					}

					handler.actions.forEach((subAction, i) => {
						var _a;
						const dropdownItem = document.createElement('div');
						dropdownItem.classList.add('bytemd-dropdown-item');
						dropdownItem.setAttribute(tippyPathKey, [...paths, i].join('-'));

						if (((_a = subAction.handler) === null || _a === void 0
						? void 0
						: _a.type) === 'dropdown') {
							dropdownItem.classList.add(tippyClass);
						}

						if (reference.classList.contains(tippyClassRight)) {
							dropdownItem.classList.add(tippyClassRight);
						}

						// div.setAttribute('data-tippy-placement', 'right');
						dropdownItem.innerHTML = `${subAction.icon
						? `<div class="bytemd-dropdown-item-icon">${subAction.icon}</div>`
						: ''}<div class="bytemd-dropdown-item-title">${subAction.title}</div>`;

						dropdown.appendChild(dropdownItem);
					});

					setProps({
						allowHTML: true,
						showOnCreate: true,
						theme: 'light-border',
						placement: 'bottom-start',
						interactive: true,
						interactiveDebounce: 50,
						arrow: false,
						offset: [0, 4],
						content: dropdown.outerHTML,
						onHidden(ins) {
							ins.destroy();
						},
						onCreate(ins) {

							[...ins.popper.querySelectorAll('.bytemd-dropdown-item')].forEach((el, i) => {
								var _a;

								const actionHandler = (_a = handler.actions[i]) === null || _a === void 0
								? void 0
								: _a.handler;

								if ((actionHandler === null || actionHandler === void 0
								? void 0
								: actionHandler.type) === 'action') {
									const { mouseenter, mouseleave } = actionHandler;

									if (mouseenter) {
										el.addEventListener('mouseenter', () => {
											mouseenter(context);
										});
									}

									if (mouseleave) {
										el.addEventListener('mouseleave', () => {
											mouseleave(context);
										});
									}
								}
							});
						}
					});
				}
			}
		});
	}

	onMount(() => {
		init();
	});

	function handleClick(e) {
		var _a, _b;
		const target = e.target.closest(`[${tippyPathKey}]`);
		if (!target) return;

		const handler = (_b = (_a = getPayloadFromElement(target)) === null || _a === void 0
		? void 0
		: _a.item) === null || _b === void 0
		? void 0
		: _b.handler;

		if ((handler === null || handler === void 0
		? void 0
		: handler.type) === 'action') {
			handler.click(context);
		}

		delegateInstance === null || delegateInstance === void 0
		? void 0
		: delegateInstance.destroy();

		init();
	}

	const click_handler = () => dispatch('tab', 'write');
	const click_handler_1 = () => dispatch('tab', 'preview');

	function div2_binding($$value) {
		binding_callbacks[$$value ? 'unshift' : 'push'](() => {
			toolbar = $$value;
			$$invalidate(4, toolbar);
		});
	}

	$$self.$$set = $$props => {
		if ('context' in $$props) $$invalidate(8, context = $$props.context);
		if ('split' in $$props) $$invalidate(0, split = $$props.split);
		if ('activeTab' in $$props) $$invalidate(1, activeTab = $$props.activeTab);
		if ('fullscreen' in $$props) $$invalidate(9, fullscreen = $$props.fullscreen);
		if ('sidebar' in $$props) $$invalidate(10, sidebar = $$props.sidebar);
		if ('locale' in $$props) $$invalidate(2, locale = $$props.locale);
		if ('actions' in $$props) $$invalidate(3, actions = $$props.actions);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*sidebar*/ 1024) {
			$$invalidate(14, tocActive = sidebar === 'toc');
		}

		if ($$self.$$.dirty & /*sidebar*/ 1024) {
			$$invalidate(13, helpActive = sidebar === 'help');
		}

		if ($$self.$$.dirty & /*activeTab*/ 2) {
			$$invalidate(12, writeActive = activeTab === 'write');
		}

		if ($$self.$$.dirty & /*activeTab*/ 2) {
			$$invalidate(11, previewActive = activeTab === 'preview');
		}

		if ($$self.$$.dirty & /*tocActive, locale, helpActive, writeActive, split, previewActive, fullscreen*/ 31237) {
			$$invalidate(5, rightActions = [
				// {
				//   title: 'Key binding',
				//   icon: icons.keyboard,
				//   handler: {
				//     type: 'dropdown',
				//     actions: [
				//       {
				//         title: 'Normal',
				//         handler: {
				//           type: 'action',
				//           click() {
				//             dispatch('key', 'default');
				//           },
				//         },
				//       },
				//       {
				//         title: 'Vim',
				//         handler: {
				//           type: 'action',
				//           click() {
				//             dispatch('key', 'vim');
				//           },
				//         },
				//       },
				//       {
				//         title: 'Emacs',
				//         handler: {
				//           type: 'action',
				//           click() {
				//             dispatch('key', 'emacs');
				//           },
				//         },
				//       },
				//     ],
				//   },
				// },
				{
					title: tocActive ? locale.closeToc : locale.toc,
					icon: icons.toc,
					handler: {
						type: 'action',
						click() {
							dispatch('click', 'toc');
						}
					},
					active: tocActive
				},
				{
					title: helpActive ? locale.closeHelp : locale.help,
					icon: icons.help,
					handler: {
						type: 'action',
						click() {
							dispatch('click', 'help');
						}
					},
					active: helpActive
				},
				{
					title: writeActive ? locale.exitWriteOnly : locale.writeOnly,
					icon: icons.left,
					handler: {
						type: 'action',
						click() {
							dispatch('tab', 'write');
						}
					},
					active: writeActive,
					hidden: !split
				},
				{
					title: previewActive
					? locale.exitPreviewOnly
					: locale.previewOnly,
					icon: icons.right,
					handler: {
						type: 'action',
						click() {
							dispatch('tab', 'preview');
						}
					},
					active: previewActive,
					hidden: !split
				},
				{
					title: fullscreen ? locale.exitFullscreen : locale.fullscreen,
					icon: fullscreen ? icons.fullscreenOff : icons.fullscreenOn,
					handler: {
						type: 'action',
						click() {
							dispatch('click', 'fullscreen');
						}
					}
				},
				{
					title: locale.source,
					icon: icons.source,
					handler: {
						type: 'action',
						click() {
							window.open('https://github.com/bytedance/bytemd');
						}
					}
				}
			]);
		}
	};

	return [
		split,
		activeTab,
		locale,
		actions,
		toolbar,
		rightActions,
		dispatch,
		handleClick,
		context,
		fullscreen,
		sidebar,
		previewActive,
		writeActive,
		helpActive,
		tocActive,
		click_handler,
		click_handler_1,
		div2_binding
	];
}

class Toolbar extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$5, create_fragment$5, not_equal, {
			context: 8,
			split: 0,
			activeTab: 1,
			fullscreen: 9,
			sidebar: 10,
			locale: 2,
			actions: 3
		});
	}
}

const schemaStr = JSON.stringify(ghSchema);
/**
 * Get unified processor with ByteMD plugins
 */
function getProcessor({ sanitize, plugins, }) {
    let p = unified().use(remarkParse);
    plugins === null || plugins === void 0 ? void 0 : plugins.forEach(({ remark }) => {
        if (remark)
            p = remark(p);
    });
    p = p.use(remarkRehype, { allowDangerousHtml: true }).use(rehypeRaw);
    let schema = JSON.parse(schemaStr);
    schema.attributes['*'].push('className'); // Allow class names by default
    if (typeof sanitize === 'function') {
        schema = sanitize(schema);
    }
    p = p.use(rehypeSanitize, schema);
    plugins === null || plugins === void 0 ? void 0 : plugins.forEach(({ rehype }) => {
        if (rehype)
            p = rehype(p);
    });
    return p.use(rehypeStringify);
}

/* packages/bytemd/lib/viewer.svelte generated by Svelte v3.44.2 */

function create_fragment$4(ctx) {
	let div;

	return {
		c() {
			div = element("div");
			attr(div, "class", "markdown-body");
		},
		m(target, anchor) {
			insert(target, div, anchor);
			div.innerHTML = /*html*/ ctx[1];
			/*div_binding*/ ctx[7](div);
		},
		p(ctx, [dirty]) {
			if (dirty & /*html*/ 2) div.innerHTML = /*html*/ ctx[1];		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
			/*div_binding*/ ctx[7](null);
		}
	};
}

function instance$4($$self, $$props, $$invalidate) {
	let html;
	const dispatch = createEventDispatcher();
	let { value = '' } = $$props;
	let { plugins = [] } = $$props;
	let { sanitize } = $$props;
	let markdownBody;
	let cbs = [];

	function on() {
		// console.log('von')
		cbs = plugins.map(p => {
			var _a;

			return (_a = p.viewerEffect) === null || _a === void 0
			? void 0
			: _a.call(p, { markdownBody, file });
		});
	}

	function off() {
		// console.log('voff')
		cbs.forEach(cb => cb === null || cb === void 0 ? void 0 : cb());
	}

	onMount(() => {
		markdownBody.addEventListener('click', e => {
			var _a;
			const $ = e.target;
			if ($.tagName !== 'A') return;
			const href = $.getAttribute('href');

			if (!(href === null || href === void 0
			? void 0
			: href.startsWith('#'))) return;

			(_a = markdownBody.querySelector('#user-content-' + href.slice(1))) === null || _a === void 0
			? void 0
			: _a.scrollIntoView();
		});
	});

	onDestroy(off);
	let file;
	let i = 0;

	afterUpdate(() => {
		// TODO: `off` should be called before DOM update
		// https://github.com/sveltejs/svelte/issues/6016
		off();

		on();
	});

	function div_binding($$value) {
		binding_callbacks[$$value ? 'unshift' : 'push'](() => {
			markdownBody = $$value;
			$$invalidate(0, markdownBody);
		});
	}

	$$self.$$set = $$props => {
		if ('value' in $$props) $$invalidate(2, value = $$props.value);
		if ('plugins' in $$props) $$invalidate(3, plugins = $$props.plugins);
		if ('sanitize' in $$props) $$invalidate(4, sanitize = $$props.sanitize);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*sanitize, plugins, value, i*/ 92) {
			try {
				$$invalidate(5, file = getProcessor({
					sanitize,
					plugins: [
						...plugins,
						{
							// remark: (p) =>
							//   p.use(() => (tree) =>{
							//     console.log(tree)
							//   }),
							rehype: p => p.use(() => (tree, file) => {
								tick().then(() => {
									// console.log(tree);
									dispatch('hast', { hast: tree, file });
								});
							})
						}
					]
				}).processSync(value));

				$$invalidate(6, i++, i);
			} catch(err) {
				console.error(err);
			}
		}

		if ($$self.$$.dirty & /*file, i*/ 96) {
			$$invalidate(1, html = `${file}<!--${i}-->`);
		}
	};

	return [markdownBody, html, value, plugins, sanitize, file, i, div_binding];
}

class Viewer extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$4, create_fragment$4, not_equal, { value: 2, plugins: 3, sanitize: 4 });
	}
}

/* packages/bytemd/lib/toc.svelte generated by Svelte v3.44.2 */

function get_each_context$1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[10] = list[i];
	child_ctx[12] = i;
	return child_ctx;
}

// (40:89) {#each items as item, index}
function create_each_block$1(ctx) {
	let li;
	let t_value = /*item*/ ctx[10].text + "";
	let t;
	let li_class_value;
	let li_style_value;
	let mounted;
	let dispose;

	function click_handler() {
		return /*click_handler*/ ctx[8](/*index*/ ctx[12]);
	}

	return {
		c() {
			li = element("li");
			t = text(t_value);
			attr(li, "class", li_class_value = `bytemd-toc-${/*item*/ ctx[10].level}`);
			attr(li, "style", li_style_value = `padding-left:${(/*item*/ ctx[10].level - /*minLevel*/ ctx[3]) * 16 + 8}px`);
			toggle_class(li, "bytemd-toc-active", /*currentHeadingIndex*/ ctx[4] === /*index*/ ctx[12]);
			toggle_class(li, "bytemd-toc-first", /*item*/ ctx[10].level === /*minLevel*/ ctx[3]);
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, t);

			if (!mounted) {
				dispose = listen(li, "click", click_handler);
				mounted = true;
			}
		},
		p(new_ctx, dirty) {
			ctx = new_ctx;
			if (dirty & /*items*/ 4 && t_value !== (t_value = /*item*/ ctx[10].text + "")) set_data(t, t_value);

			if (dirty & /*items*/ 4 && li_class_value !== (li_class_value = `bytemd-toc-${/*item*/ ctx[10].level}`)) {
				attr(li, "class", li_class_value);
			}

			if (dirty & /*items, minLevel*/ 12 && li_style_value !== (li_style_value = `padding-left:${(/*item*/ ctx[10].level - /*minLevel*/ ctx[3]) * 16 + 8}px`)) {
				attr(li, "style", li_style_value);
			}

			if (dirty & /*items, currentHeadingIndex*/ 20) {
				toggle_class(li, "bytemd-toc-active", /*currentHeadingIndex*/ ctx[4] === /*index*/ ctx[12]);
			}

			if (dirty & /*items, items, minLevel*/ 12) {
				toggle_class(li, "bytemd-toc-first", /*item*/ ctx[10].level === /*minLevel*/ ctx[3]);
			}
		},
		d(detaching) {
			if (detaching) detach(li);
			mounted = false;
			dispose();
		}
	};
}

function create_fragment$3(ctx) {
	let div;
	let h2;
	let t_value = /*locale*/ ctx[0].toc + "";
	let t;
	let ul;
	let each_value = /*items*/ ctx[2];
	let each_blocks = [];

	for (let i = 0; i < each_value.length; i += 1) {
		each_blocks[i] = create_each_block$1(get_each_context$1(ctx, each_value, i));
	}

	return {
		c() {
			div = element("div");
			h2 = element("h2");
			t = text(t_value);
			ul = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(div, "class", "bytemd-toc");
			toggle_class(div, "bytemd-hidden", !/*visible*/ ctx[1]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, h2);
			append(h2, t);
			append(div, ul);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul, null);
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*locale*/ 1 && t_value !== (t_value = /*locale*/ ctx[0].toc + "")) set_data(t, t_value);

			if (dirty & /*items, minLevel, currentHeadingIndex, dispatch*/ 60) {
				each_value = /*items*/ ctx[2];
				let i;

				for (i = 0; i < each_value.length; i += 1) {
					const child_ctx = get_each_context$1(ctx, each_value, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block$1(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(ul, null);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value.length;
			}

			if (dirty & /*visible*/ 2) {
				toggle_class(div, "bytemd-hidden", !/*visible*/ ctx[1]);
			}
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
			destroy_each(each_blocks, detaching);
		}
	};
}

function instance$3($$self, $$props, $$invalidate) {
	let { hast } = $$props;
	let { currentBlockIndex } = $$props;
	let { locale } = $$props;
	let { visible } = $$props;
	const dispatch = createEventDispatcher();
	let items;
	let minLevel = 6;
	let currentHeadingIndex = 0;

	function stringifyHeading(e) {
		let result = '';

		visit(e, node => {
			if (node.type === 'text') {
				result += node.value;
			}
		});

		return result;
	}

	const click_handler = index => {
		dispatch('click', index);
	};

	$$self.$$set = $$props => {
		if ('hast' in $$props) $$invalidate(6, hast = $$props.hast);
		if ('currentBlockIndex' in $$props) $$invalidate(7, currentBlockIndex = $$props.currentBlockIndex);
		if ('locale' in $$props) $$invalidate(0, locale = $$props.locale);
		if ('visible' in $$props) $$invalidate(1, visible = $$props.visible);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*hast, minLevel, items, currentBlockIndex*/ 204) {
			(() => {
				$$invalidate(2, items = []);
				$$invalidate(4, currentHeadingIndex = 0);

				hast.children.filter(v => v.type === 'element').forEach((node, index) => {
					if (node.tagName[0] === 'h' && !!node.children.length) {
						const i = Number(node.tagName[1]);
						$$invalidate(3, minLevel = Math.min(minLevel, i));
						items.push({ level: i, text: stringifyHeading(node) });
					}

					// console.log(currentBlockIndex, index);
					if (currentBlockIndex >= index) {
						$$invalidate(4, currentHeadingIndex = items.length - 1);
					}
				});
			})();
		}
	};

	return [
		locale,
		visible,
		items,
		minLevel,
		currentHeadingIndex,
		dispatch,
		hast,
		currentBlockIndex,
		click_handler
	];
}

class Toc extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$3, create_fragment$3, not_equal, {
			hast: 6,
			currentBlockIndex: 7,
			locale: 0,
			visible: 1
		});
	}
}

function createEditorUtils(codemirror, editor) {
    return {
        /**
         * Wrap text with decorators, for example:
         *
         * `text -> *text*`
         */
        wrapText(before, after = before) {
            const range = editor.somethingSelected()
                ? editor.listSelections()[0] // only handle the first selection
                : editor.findWordAt(editor.getCursor());
            const from = range.from(); // use from/to instead of anchor/head for reverse select
            const to = range.to();
            const text = editor.getRange(from, to);
            const fromBefore = codemirror.Pos(from.line, from.ch - before.length);
            const toAfter = codemirror.Pos(to.line, to.ch + after.length);
            if (editor.getRange(fromBefore, from) === before &&
                editor.getRange(to, toAfter) === after) {
                editor.replaceRange(text, fromBefore, toAfter);
                editor.setSelection(fromBefore, codemirror.Pos(fromBefore.line, fromBefore.ch + text.length));
            }
            else {
                editor.replaceRange(before + text + after, from, to);
                // select the original text
                const cursor = editor.getCursor();
                editor.setSelection(codemirror.Pos(cursor.line, cursor.ch - after.length - text.length), codemirror.Pos(cursor.line, cursor.ch - after.length));
            }
        },
        /**
         * replace multiple lines
         *
         * `line -> # line`
         */
        replaceLines(replace) {
            const [selection] = editor.listSelections();
            const range = [
                codemirror.Pos(selection.from().line, 0),
                codemirror.Pos(selection.to().line),
            ];
            const lines = editor.getRange(...range).split('\n');
            editor.replaceRange(lines.map(replace).join('\n'), ...range);
            editor.setSelection(...range);
        },
        /**
         * Append a block based on the cursor position
         */
        appendBlock(content) {
            const cursor = editor.getCursor();
            // find the first blank line
            let emptyLine = -1;
            for (let i = cursor.line; i < editor.lineCount(); i++) {
                if (!editor.getLine(i).trim()) {
                    emptyLine = i;
                    break;
                }
            }
            if (emptyLine === -1) {
                // insert a new line to the bottom
                editor.replaceRange('\n', codemirror.Pos(editor.lineCount()));
                emptyLine = editor.lineCount();
            }
            editor.replaceRange('\n' + content, codemirror.Pos(emptyLine));
            return codemirror.Pos(emptyLine + 1, 0);
        },
        /**
         * Triggers a virtual file input and let user select files
         *
         * https://www.npmjs.com/package/select-files
         */
        selectFiles,
    };
}
function findStartIndex(num, nums) {
    let startIndex = nums.length - 2;
    for (let i = 0; i < nums.length; i++) {
        if (num < nums[i]) {
            startIndex = i - 1;
            break;
        }
    }
    startIndex = Math.max(startIndex, 0); // ensure >= 0
    return startIndex;
}
const getShortcutWithPrefix = (key, shift = false) => {
    const shiftPrefix = shift ? 'Shift-' : '';
    const CmdPrefix = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)
        ? 'Cmd-'
        : 'Ctrl-';
    return shiftPrefix + CmdPrefix + key;
};
async function handleImageUpload({ editor, appendBlock, codemirror }, uploadImages, files) {
    const imgs = await uploadImages(files);
    const pos = appendBlock(imgs
        .map(({ url, alt, title }, i) => {
        alt = alt !== null && alt !== void 0 ? alt : files[i].name;
        return `![${alt}](${url}${title ? ` "${title}"` : ''})`;
    })
        .join('\n\n'));
    editor.setSelection(pos, codemirror.Pos(pos.line + imgs.length * 2 - 2));
    editor.focus();
}
function getBuiltinActions(locale, plugins, uploadImages) {
    const items = [
        {
            icon: icons.heading,
            handler: {
                type: 'dropdown',
                actions: [1, 2, 3, 4, 5, 6].map((level) => ({
                    title: locale[`h${level}`],
                    icon: icons[`h${level}`],
                    cheatsheet: level <= 3
                        ? `${'#'.repeat(level)} ${locale.headingText}`
                        : undefined,
                    handler: {
                        type: 'action',
                        click({ replaceLines, editor }) {
                            replaceLines((line) => {
                                line = line.trim().replace(/^#*/, '').trim();
                                line = '#'.repeat(level) + ' ' + line;
                                return line;
                            });
                            editor.focus();
                        },
                    },
                })),
            },
        },
        {
            title: locale.bold,
            icon: icons.bold,
            cheatsheet: `**${locale.boldText}**`,
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('B'),
                click({ wrapText, editor }) {
                    wrapText('**');
                    editor.focus();
                },
            },
        },
        {
            title: locale.italic,
            icon: icons.italic,
            cheatsheet: `*${locale.italicText}*`,
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('I'),
                click({ wrapText, editor }) {
                    wrapText('*');
                    editor.focus();
                },
            },
        },
        {
            title: locale.quote,
            icon: icons.quote,
            cheatsheet: `> ${locale.quotedText}`,
            handler: {
                type: 'action',
                click({ replaceLines, editor }) {
                    replaceLines((line) => '> ' + line);
                    editor.focus();
                },
            },
        },
        {
            title: locale.link,
            icon: icons.link,
            cheatsheet: `[${locale.linkText}](url)`,
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('K'),
                click({ editor, wrapText, codemirror }) {
                    wrapText('[', '](url)');
                    const cursor = editor.getCursor();
                    editor.setSelection(codemirror.Pos(cursor.line, cursor.ch + 2), codemirror.Pos(cursor.line, cursor.ch + 5));
                    editor.focus();
                },
            },
        },
        {
            title: locale.image,
            icon: icons.image,
            cheatsheet: `![${locale.imageAlt}](url "${locale.imageTitle}")`,
            handler: uploadImages
                ? {
                    type: 'action',
                    shortcut: getShortcutWithPrefix('I', true),
                    async click(ctx) {
                        const fileList = await selectFiles({
                            accept: 'image/*',
                            multiple: true,
                        });
                        if (fileList === null || fileList === void 0 ? void 0 : fileList.length) {
                            await handleImageUpload(ctx, uploadImages, Array.from(fileList));
                        }
                    },
                }
                : undefined,
        },
        {
            title: locale.code,
            icon: icons.code,
            cheatsheet: '`' + locale.codeText + '`',
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('K', true),
                click({ wrapText, editor }) {
                    wrapText('`');
                    editor.focus();
                },
            },
        },
        {
            title: locale.codeBlock,
            icon: icons.codeBlock,
            cheatsheet: '```' + locale.codeLang + '↵',
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('C', true),
                click({ editor, appendBlock, codemirror }) {
                    const pos = appendBlock('```js\n```');
                    editor.setSelection(codemirror.Pos(pos.line, 3), codemirror.Pos(pos.line, 5));
                    editor.focus();
                },
            },
        },
        {
            title: locale.ul,
            icon: icons.ul,
            cheatsheet: `- ${locale.ulItem}`,
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('U', true),
                click({ replaceLines, editor }) {
                    replaceLines((line) => '- ' + line);
                    editor.focus();
                },
            },
        },
        {
            title: locale.ol,
            icon: icons.ol,
            cheatsheet: `1. ${locale.olItem}`,
            handler: {
                type: 'action',
                shortcut: getShortcutWithPrefix('O', true),
                click({ replaceLines, editor }) {
                    replaceLines((line, i) => `${i + 1}. ${line}`);
                    editor.focus();
                },
            },
        },
        {
            title: locale.hr,
            icon: icons.hr,
            cheatsheet: '---',
        },
    ];
    plugins.forEach(({ actions }) => {
        if (actions)
            items.push(...actions);
    });
    return items;
}

/* packages/bytemd/lib/status.svelte generated by Svelte v3.44.2 */

function create_if_block_1$1(ctx) {
	let span;
	let t_value = /*locale*/ ctx[2].limited + "";
	let t;

	return {
		c() {
			span = element("span");
			t = text(t_value);
			attr(span, "class", "bytemd-status-error");
		},
		m(target, anchor) {
			insert(target, span, anchor);
			append(span, t);
		},
		p(ctx, dirty) {
			if (dirty & /*locale*/ 4 && t_value !== (t_value = /*locale*/ ctx[2].limited + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(span);
		}
	};
}

// (11:290) {#if showSync}
function create_if_block$2(ctx) {
	let label;
	let input;
	let t_value = /*locale*/ ctx[2].sync + "";
	let t;
	let mounted;
	let dispose;

	return {
		c() {
			label = element("label");
			input = element("input");
			t = text(t_value);
			attr(input, "type", "checkbox");
			input.checked = /*syncEnabled*/ ctx[1];
		},
		m(target, anchor) {
			insert(target, label, anchor);
			append(label, input);
			append(label, t);

			if (!mounted) {
				dispose = listen(input, "change", /*change_handler*/ ctx[8]);
				mounted = true;
			}
		},
		p(ctx, dirty) {
			if (dirty & /*syncEnabled*/ 2) {
				input.checked = /*syncEnabled*/ ctx[1];
			}

			if (dirty & /*locale*/ 4 && t_value !== (t_value = /*locale*/ ctx[2].sync + "")) set_data(t, t_value);
		},
		d(detaching) {
			if (detaching) detach(label);
			mounted = false;
			dispose();
		}
	};
}

function create_fragment$2(ctx) {
	let div2;
	let div0;
	let span0;
	let t0_value = /*locale*/ ctx[2].words + "";
	let t0;
	let t1;
	let strong0;
	let t2;
	let span1;
	let t3_value = /*locale*/ ctx[2].lines + "";
	let t3;
	let t4;
	let strong1;
	let t5;
	let div1;
	let span2;
	let t6_value = /*locale*/ ctx[2].top + "";
	let t6;
	let mounted;
	let dispose;
	let if_block0 = /*islimited*/ ctx[3] && create_if_block_1$1(ctx);
	let if_block1 = /*showSync*/ ctx[0] && create_if_block$2(ctx);

	return {
		c() {
			div2 = element("div");
			div0 = element("div");
			span0 = element("span");
			t0 = text(t0_value);
			t1 = text(": ");
			strong0 = element("strong");
			t2 = text(/*words*/ ctx[5]);
			span1 = element("span");
			t3 = text(t3_value);
			t4 = text(": ");
			strong1 = element("strong");
			t5 = text(/*lines*/ ctx[4]);
			if (if_block0) if_block0.c();
			div1 = element("div");
			if (if_block1) if_block1.c();
			span2 = element("span");
			t6 = text(t6_value);
			attr(div0, "class", "bytemd-status-left");
			attr(div1, "class", "bytemd-status-right");
			attr(div2, "class", "bytemd-status");
		},
		m(target, anchor) {
			insert(target, div2, anchor);
			append(div2, div0);
			append(div0, span0);
			append(span0, t0);
			append(span0, t1);
			append(span0, strong0);
			append(strong0, t2);
			append(div0, span1);
			append(span1, t3);
			append(span1, t4);
			append(span1, strong1);
			append(strong1, t5);
			if (if_block0) if_block0.m(div0, null);
			append(div2, div1);
			if (if_block1) if_block1.m(div1, null);
			append(div1, span2);
			append(span2, t6);

			if (!mounted) {
				dispose = listen(span2, "click", /*click_handler*/ ctx[9]);
				mounted = true;
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*locale*/ 4 && t0_value !== (t0_value = /*locale*/ ctx[2].words + "")) set_data(t0, t0_value);
			if (dirty & /*words*/ 32) set_data(t2, /*words*/ ctx[5]);
			if (dirty & /*locale*/ 4 && t3_value !== (t3_value = /*locale*/ ctx[2].lines + "")) set_data(t3, t3_value);
			if (dirty & /*lines*/ 16) set_data(t5, /*lines*/ ctx[4]);

			if (/*islimited*/ ctx[3]) {
				if (if_block0) {
					if_block0.p(ctx, dirty);
				} else {
					if_block0 = create_if_block_1$1(ctx);
					if_block0.c();
					if_block0.m(div0, null);
				}
			} else if (if_block0) {
				if_block0.d(1);
				if_block0 = null;
			}

			if (/*showSync*/ ctx[0]) {
				if (if_block1) {
					if_block1.p(ctx, dirty);
				} else {
					if_block1 = create_if_block$2(ctx);
					if_block1.c();
					if_block1.m(div1, span2);
				}
			} else if (if_block1) {
				if_block1.d(1);
				if_block1 = null;
			}

			if (dirty & /*locale*/ 4 && t6_value !== (t6_value = /*locale*/ ctx[2].top + "")) set_data(t6, t6_value);
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div2);
			if (if_block0) if_block0.d();
			if (if_block1) if_block1.d();
			mounted = false;
			dispose();
		}
	};
}

function instance$2($$self, $$props, $$invalidate) {
	let words;
	let lines;
	let { showSync } = $$props;
	let { value } = $$props;
	let { syncEnabled } = $$props;
	let { locale } = $$props;
	let { islimited } = $$props;
	const dispatch = createEventDispatcher();
	const change_handler = () => dispatch('sync', !syncEnabled);
	const click_handler = () => dispatch('top');

	$$self.$$set = $$props => {
		if ('showSync' in $$props) $$invalidate(0, showSync = $$props.showSync);
		if ('value' in $$props) $$invalidate(7, value = $$props.value);
		if ('syncEnabled' in $$props) $$invalidate(1, syncEnabled = $$props.syncEnabled);
		if ('locale' in $$props) $$invalidate(2, locale = $$props.locale);
		if ('islimited' in $$props) $$invalidate(3, islimited = $$props.islimited);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*value*/ 128) {
			$$invalidate(5, words = wordCount(value));
		}

		if ($$self.$$.dirty & /*value*/ 128) {
			$$invalidate(4, lines = value.split('\n').length);
		}
	};

	return [
		showSync,
		syncEnabled,
		locale,
		islimited,
		lines,
		words,
		dispatch,
		value,
		change_handler,
		click_handler
	];
}

class Status extends SvelteComponent {
	constructor(options) {
		super();

		init(this, options, instance$2, create_fragment$2, safe_not_equal, {
			showSync: 0,
			value: 7,
			syncEnabled: 1,
			locale: 2,
			islimited: 3
		});
	}
}

var write = "Write";
var preview = "Preview";
var writeOnly = "Write only";
var exitWriteOnly = "Exit write only";
var previewOnly = "Preview only";
var exitPreviewOnly = "Exit preview only";
var help = "Help";
var closeHelp = "Close help";
var toc = "Table of contents";
var closeToc = "Close table of contents";
var fullscreen = "Fullscreen";
var exitFullscreen = "Exit fullscreen";
var source = "Source code";
var cheatsheet = "Markdown Cheatsheet";
var shortcuts = "Shortcuts";
var words = "Words";
var lines = "Lines";
var sync = "Scroll sync";
var top = "Scroll to top";
var limited = "The maximum character limit has been reached";
var h1 = "Heading 1";
var h2 = "Heading 2";
var h3 = "Heading 3";
var h4 = "Heading 4";
var h5 = "Heading 5";
var h6 = "Heading 6";
var headingText = "heading";
var bold = "Bold";
var boldText = "bold text";
var italic = "Italic";
var italicText = "italic text";
var quote = "Quote";
var quotedText = "quoted text";
var link = "Link";
var linkText = "link text";
var image = "Image";
var imageAlt = "alt";
var imageTitle = "title";
var code = "Code";
var codeText = "code";
var codeBlock = "Code block";
var codeLang = "lang";
var ul = "Unordered list";
var ulItem = "item";
var ol = "Ordered list";
var olItem = "item";
var hr = "Horizontal rule";
var en = {
	write: write,
	preview: preview,
	writeOnly: writeOnly,
	exitWriteOnly: exitWriteOnly,
	previewOnly: previewOnly,
	exitPreviewOnly: exitPreviewOnly,
	help: help,
	closeHelp: closeHelp,
	toc: toc,
	closeToc: closeToc,
	fullscreen: fullscreen,
	exitFullscreen: exitFullscreen,
	source: source,
	cheatsheet: cheatsheet,
	shortcuts: shortcuts,
	words: words,
	lines: lines,
	sync: sync,
	top: top,
	limited: limited,
	h1: h1,
	h2: h2,
	h3: h3,
	h4: h4,
	h5: h5,
	h6: h6,
	headingText: headingText,
	bold: bold,
	boldText: boldText,
	italic: italic,
	italicText: italicText,
	quote: quote,
	quotedText: quotedText,
	link: link,
	linkText: linkText,
	image: image,
	imageAlt: imageAlt,
	imageTitle: imageTitle,
	code: code,
	codeText: codeText,
	codeBlock: codeBlock,
	codeLang: codeLang,
	ul: ul,
	ulItem: ulItem,
	ol: ol,
	olItem: olItem,
	hr: hr
};

/* packages/bytemd/lib/help.svelte generated by Svelte v3.44.2 */

function get_each_context(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[5] = list[i];
	return child_ctx;
}

function get_each_context_1(ctx, list, i) {
	const child_ctx = ctx.slice();
	child_ctx[5] = list[i];
	return child_ctx;
}

// (18:120) {#if action.cheatsheet}
function create_if_block_1(ctx) {
	let li;
	let div0;
	let raw_value = /*action*/ ctx[5].icon + "";
	let div1;
	let t0_value = /*action*/ ctx[5].title + "";
	let t0;
	let div2;
	let code;
	let t1_value = /*action*/ ctx[5].cheatsheet + "";
	let t1;

	return {
		c() {
			li = element("li");
			div0 = element("div");
			div1 = element("div");
			t0 = text(t0_value);
			div2 = element("div");
			code = element("code");
			t1 = text(t1_value);
			attr(div0, "class", "bytemd-help-icon");
			attr(div1, "class", "bytemd-help-title");
			attr(div2, "class", "bytemd-help-content");
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, div0);
			div0.innerHTML = raw_value;
			append(li, div1);
			append(div1, t0);
			append(li, div2);
			append(div2, code);
			append(code, t1);
		},
		p(ctx, dirty) {
			if (dirty & /*items*/ 4 && raw_value !== (raw_value = /*action*/ ctx[5].icon + "")) div0.innerHTML = raw_value;			if (dirty & /*items*/ 4 && t0_value !== (t0_value = /*action*/ ctx[5].title + "")) set_data(t0, t0_value);
			if (dirty & /*items*/ 4 && t1_value !== (t1_value = /*action*/ ctx[5].cheatsheet + "")) set_data(t1, t1_value);
		},
		d(detaching) {
			if (detaching) detach(li);
		}
	};
}

// (18:97) {#each items as action}
function create_each_block_1(ctx) {
	let if_block_anchor;
	let if_block = /*action*/ ctx[5].cheatsheet && create_if_block_1(ctx);

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
		},
		p(ctx, dirty) {
			if (/*action*/ ctx[5].cheatsheet) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block_1(ctx);
					if_block.c();
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

// (18:400) {#if action.handler && action.handler.type === 'action' && action.handler.shortcut}
function create_if_block$1(ctx) {
	let li;
	let div0;
	let raw_value = /*action*/ ctx[5].icon + "";
	let div1;
	let t0_value = /*action*/ ctx[5].title + "";
	let t0;
	let div2;
	let kbd;
	let t1_value = /*action*/ ctx[5].handler.shortcut + "";
	let t1;

	return {
		c() {
			li = element("li");
			div0 = element("div");
			div1 = element("div");
			t0 = text(t0_value);
			div2 = element("div");
			kbd = element("kbd");
			t1 = text(t1_value);
			attr(div0, "class", "bytemd-help-icon");
			attr(div1, "class", "bytemd-help-title");
			attr(div2, "class", "bytemd-help-content");
		},
		m(target, anchor) {
			insert(target, li, anchor);
			append(li, div0);
			div0.innerHTML = raw_value;
			append(li, div1);
			append(div1, t0);
			append(li, div2);
			append(div2, kbd);
			append(kbd, t1);
		},
		p(ctx, dirty) {
			if (dirty & /*items*/ 4 && raw_value !== (raw_value = /*action*/ ctx[5].icon + "")) div0.innerHTML = raw_value;			if (dirty & /*items*/ 4 && t0_value !== (t0_value = /*action*/ ctx[5].title + "")) set_data(t0, t0_value);
			if (dirty & /*items*/ 4 && t1_value !== (t1_value = /*action*/ ctx[5].handler.shortcut + "")) set_data(t1, t1_value);
		},
		d(detaching) {
			if (detaching) detach(li);
		}
	};
}

// (18:377) {#each items as action}
function create_each_block(ctx) {
	let if_block_anchor;
	let if_block = /*action*/ ctx[5].handler && /*action*/ ctx[5].handler.type === 'action' && /*action*/ ctx[5].handler.shortcut && create_if_block$1(ctx);

	return {
		c() {
			if (if_block) if_block.c();
			if_block_anchor = empty();
		},
		m(target, anchor) {
			if (if_block) if_block.m(target, anchor);
			insert(target, if_block_anchor, anchor);
		},
		p(ctx, dirty) {
			if (/*action*/ ctx[5].handler && /*action*/ ctx[5].handler.type === 'action' && /*action*/ ctx[5].handler.shortcut) {
				if (if_block) {
					if_block.p(ctx, dirty);
				} else {
					if_block = create_if_block$1(ctx);
					if_block.c();
					if_block.m(if_block_anchor.parentNode, if_block_anchor);
				}
			} else if (if_block) {
				if_block.d(1);
				if_block = null;
			}
		},
		d(detaching) {
			if (if_block) if_block.d(detaching);
			if (detaching) detach(if_block_anchor);
		}
	};
}

function create_fragment$1(ctx) {
	let div;
	let h20;
	let t0_value = /*locale*/ ctx[0].cheatsheet + "";
	let t0;
	let ul0;
	let h21;
	let t1_value = /*locale*/ ctx[0].shortcuts + "";
	let t1;
	let ul1;
	let each_value_1 = /*items*/ ctx[2];
	let each_blocks_1 = [];

	for (let i = 0; i < each_value_1.length; i += 1) {
		each_blocks_1[i] = create_each_block_1(get_each_context_1(ctx, each_value_1, i));
	}

	let each_value = /*items*/ ctx[2];
	let each_blocks = [];

	for (let i = 0; i < each_value.length; i += 1) {
		each_blocks[i] = create_each_block(get_each_context(ctx, each_value, i));
	}

	return {
		c() {
			div = element("div");
			h20 = element("h2");
			t0 = text(t0_value);
			ul0 = element("ul");

			for (let i = 0; i < each_blocks_1.length; i += 1) {
				each_blocks_1[i].c();
			}

			h21 = element("h2");
			t1 = text(t1_value);
			ul1 = element("ul");

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].c();
			}

			attr(div, "class", "bytemd-help");
			toggle_class(div, "bytemd-hidden", !/*visible*/ ctx[1]);
		},
		m(target, anchor) {
			insert(target, div, anchor);
			append(div, h20);
			append(h20, t0);
			append(div, ul0);

			for (let i = 0; i < each_blocks_1.length; i += 1) {
				each_blocks_1[i].m(ul0, null);
			}

			append(div, h21);
			append(h21, t1);
			append(div, ul1);

			for (let i = 0; i < each_blocks.length; i += 1) {
				each_blocks[i].m(ul1, null);
			}
		},
		p(ctx, [dirty]) {
			if (dirty & /*locale*/ 1 && t0_value !== (t0_value = /*locale*/ ctx[0].cheatsheet + "")) set_data(t0, t0_value);

			if (dirty & /*items*/ 4) {
				each_value_1 = /*items*/ ctx[2];
				let i;

				for (i = 0; i < each_value_1.length; i += 1) {
					const child_ctx = get_each_context_1(ctx, each_value_1, i);

					if (each_blocks_1[i]) {
						each_blocks_1[i].p(child_ctx, dirty);
					} else {
						each_blocks_1[i] = create_each_block_1(child_ctx);
						each_blocks_1[i].c();
						each_blocks_1[i].m(ul0, null);
					}
				}

				for (; i < each_blocks_1.length; i += 1) {
					each_blocks_1[i].d(1);
				}

				each_blocks_1.length = each_value_1.length;
			}

			if (dirty & /*locale*/ 1 && t1_value !== (t1_value = /*locale*/ ctx[0].shortcuts + "")) set_data(t1, t1_value);

			if (dirty & /*items*/ 4) {
				each_value = /*items*/ ctx[2];
				let i;

				for (i = 0; i < each_value.length; i += 1) {
					const child_ctx = get_each_context(ctx, each_value, i);

					if (each_blocks[i]) {
						each_blocks[i].p(child_ctx, dirty);
					} else {
						each_blocks[i] = create_each_block(child_ctx);
						each_blocks[i].c();
						each_blocks[i].m(ul1, null);
					}
				}

				for (; i < each_blocks.length; i += 1) {
					each_blocks[i].d(1);
				}

				each_blocks.length = each_value.length;
			}

			if (dirty & /*visible*/ 2) {
				toggle_class(div, "bytemd-hidden", !/*visible*/ ctx[1]);
			}
		},
		i: noop,
		o: noop,
		d(detaching) {
			if (detaching) detach(div);
			destroy_each(each_blocks_1, detaching);
			destroy_each(each_blocks, detaching);
		}
	};
}

function instance$1($$self, $$props, $$invalidate) {
	let items;
	let { actions } = $$props;
	let { locale } = $$props;
	let { visible } = $$props;

	function flatItems(actions) {
		let items = [];

		actions.forEach(action => {
			const { handler, cheatsheet } = action;

			if ((handler === null || handler === void 0
			? void 0
			: handler.type) === 'dropdown') {
				items.push(...flatItems(handler.actions));
			}

			if (cheatsheet) {
				items.push(action);
			}
		});

		return items;
	}

	$$self.$$set = $$props => {
		if ('actions' in $$props) $$invalidate(3, actions = $$props.actions);
		if ('locale' in $$props) $$invalidate(0, locale = $$props.locale);
		if ('visible' in $$props) $$invalidate(1, visible = $$props.visible);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty & /*actions*/ 8) {
			$$invalidate(2, items = flatItems(actions));
		}
	};

	return [locale, visible, items, actions];
}

class Help extends SvelteComponent {
	constructor(options) {
		super();
		init(this, options, instance$1, create_fragment$1, safe_not_equal, { actions: 3, locale: 0, visible: 1 });
	}
}

/* packages/bytemd/lib/editor.svelte generated by Svelte v3.44.2 */

function create_if_block(ctx) {
	let viewer;
	let current;

	viewer = new Viewer({
			props: {
				value: /*debouncedValue*/ ctx[15],
				plugins: /*plugins*/ ctx[1],
				sanitize: /*sanitize*/ ctx[2]
			}
		});

	viewer.$on("hast", /*hast_handler*/ ctx[34]);

	return {
		c() {
			create_component(viewer.$$.fragment);
		},
		m(target, anchor) {
			mount_component(viewer, target, anchor);
			current = true;
		},
		p(ctx, dirty) {
			const viewer_changes = {};
			if (dirty[0] & /*debouncedValue*/ 32768) viewer_changes.value = /*debouncedValue*/ ctx[15];
			if (dirty[0] & /*plugins*/ 2) viewer_changes.plugins = /*plugins*/ ctx[1];
			if (dirty[0] & /*sanitize*/ 4) viewer_changes.sanitize = /*sanitize*/ ctx[2];
			viewer.$set(viewer_changes);
		},
		i(local) {
			if (current) return;
			transition_in(viewer.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(viewer.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			destroy_component(viewer, detaching);
		}
	};
}

function create_fragment(ctx) {
	let div5;
	let toolbar;
	let div4;
	let div0;
	let div0_style_value;
	let div1;
	let div1_style_value;
	let div3;
	let div2;
	let raw_value = icons.close + "";
	let help;
	let toc;
	let status;
	let current;
	let mounted;
	let dispose;

	toolbar = new Toolbar({
			props: {
				context: /*context*/ ctx[9],
				split: /*split*/ ctx[10],
				activeTab: /*activeTab*/ ctx[7],
				sidebar: /*sidebar*/ ctx[8],
				fullscreen: /*fullscreen*/ ctx[14],
				locale: /*mergedLocale*/ ctx[11],
				actions: /*actions*/ ctx[20]
			}
		});

	toolbar.$on("key", /*key_handler*/ ctx[30]);
	toolbar.$on("tab", /*tab_handler*/ ctx[31]);
	toolbar.$on("click", /*click_handler*/ ctx[32]);
	let if_block = !/*overridePreview*/ ctx[3] && create_if_block(ctx);

	help = new Help({
			props: {
				locale: /*mergedLocale*/ ctx[11],
				actions: /*actions*/ ctx[20],
				visible: /*sidebar*/ ctx[8] === 'help'
			}
		});

	toc = new Toc({
			props: {
				hast: /*hast*/ ctx[17],
				locale: /*mergedLocale*/ ctx[11],
				currentBlockIndex: /*currentBlockIndex*/ ctx[19],
				visible: /*sidebar*/ ctx[8] === 'toc'
			}
		});

	toc.$on("click", /*click_handler_2*/ ctx[37]);

	status = new Status({
			props: {
				locale: /*mergedLocale*/ ctx[11],
				showSync: !/*overridePreview*/ ctx[3] && /*split*/ ctx[10],
				value: /*debouncedValue*/ ctx[15],
				syncEnabled: /*syncEnabled*/ ctx[16],
				islimited: /*value*/ ctx[0].length > /*maxLength*/ ctx[4]
			}
		});

	status.$on("sync", /*sync_handler*/ ctx[38]);
	status.$on("top", /*top_handler*/ ctx[39]);

	return {
		c() {
			div5 = element("div");
			create_component(toolbar.$$.fragment);
			div4 = element("div");
			div0 = element("div");
			div1 = element("div");
			if (if_block) if_block.c();
			div3 = element("div");
			div2 = element("div");
			create_component(help.$$.fragment);
			create_component(toc.$$.fragment);
			create_component(status.$$.fragment);
			attr(div0, "class", "bytemd-editor");
			attr(div0, "style", div0_style_value = /*styles*/ ctx[21].edit);
			attr(div1, "class", "bytemd-preview");
			attr(div1, "style", div1_style_value = /*styles*/ ctx[21].preview);
			attr(div2, "class", "bytemd-sidebar-close");
			attr(div3, "class", "bytemd-sidebar");
			toggle_class(div3, "bytemd-hidden", /*sidebar*/ ctx[8] === false);
			attr(div4, "class", "bytemd-body");
			attr(div5, "class", "bytemd");
			toggle_class(div5, "bytemd-split", /*split*/ ctx[10] && /*activeTab*/ ctx[7] === false);
			toggle_class(div5, "bytemd-fullscreen", /*fullscreen*/ ctx[14]);
		},
		m(target, anchor) {
			insert(target, div5, anchor);
			mount_component(toolbar, div5, null);
			append(div5, div4);
			append(div4, div0);
			/*div0_binding*/ ctx[33](div0);
			append(div4, div1);
			if (if_block) if_block.m(div1, null);
			/*div1_binding*/ ctx[35](div1);
			append(div4, div3);
			append(div3, div2);
			div2.innerHTML = raw_value;
			mount_component(help, div3, null);
			mount_component(toc, div3, null);
			mount_component(status, div5, null);
			/*div5_binding*/ ctx[40](div5);
			current = true;

			if (!mounted) {
				dispose = listen(div2, "click", /*click_handler_1*/ ctx[36]);
				mounted = true;
			}
		},
		p(ctx, dirty) {
			const toolbar_changes = {};
			if (dirty[0] & /*context*/ 512) toolbar_changes.context = /*context*/ ctx[9];
			if (dirty[0] & /*split*/ 1024) toolbar_changes.split = /*split*/ ctx[10];
			if (dirty[0] & /*activeTab*/ 128) toolbar_changes.activeTab = /*activeTab*/ ctx[7];
			if (dirty[0] & /*sidebar*/ 256) toolbar_changes.sidebar = /*sidebar*/ ctx[8];
			if (dirty[0] & /*fullscreen*/ 16384) toolbar_changes.fullscreen = /*fullscreen*/ ctx[14];
			if (dirty[0] & /*mergedLocale*/ 2048) toolbar_changes.locale = /*mergedLocale*/ ctx[11];
			if (dirty[0] & /*actions*/ 1048576) toolbar_changes.actions = /*actions*/ ctx[20];
			toolbar.$set(toolbar_changes);

			if (!current || dirty[0] & /*styles*/ 2097152 && div0_style_value !== (div0_style_value = /*styles*/ ctx[21].edit)) {
				attr(div0, "style", div0_style_value);
			}

			if (!/*overridePreview*/ ctx[3]) {
				if (if_block) {
					if_block.p(ctx, dirty);

					if (dirty[0] & /*overridePreview*/ 8) {
						transition_in(if_block, 1);
					}
				} else {
					if_block = create_if_block(ctx);
					if_block.c();
					transition_in(if_block, 1);
					if_block.m(div1, null);
				}
			} else if (if_block) {
				group_outros();

				transition_out(if_block, 1, 1, () => {
					if_block = null;
				});

				check_outros();
			}

			if (!current || dirty[0] & /*styles*/ 2097152 && div1_style_value !== (div1_style_value = /*styles*/ ctx[21].preview)) {
				attr(div1, "style", div1_style_value);
			}

			const help_changes = {};
			if (dirty[0] & /*mergedLocale*/ 2048) help_changes.locale = /*mergedLocale*/ ctx[11];
			if (dirty[0] & /*actions*/ 1048576) help_changes.actions = /*actions*/ ctx[20];
			if (dirty[0] & /*sidebar*/ 256) help_changes.visible = /*sidebar*/ ctx[8] === 'help';
			help.$set(help_changes);
			const toc_changes = {};
			if (dirty[0] & /*hast*/ 131072) toc_changes.hast = /*hast*/ ctx[17];
			if (dirty[0] & /*mergedLocale*/ 2048) toc_changes.locale = /*mergedLocale*/ ctx[11];
			if (dirty[0] & /*currentBlockIndex*/ 524288) toc_changes.currentBlockIndex = /*currentBlockIndex*/ ctx[19];
			if (dirty[0] & /*sidebar*/ 256) toc_changes.visible = /*sidebar*/ ctx[8] === 'toc';
			toc.$set(toc_changes);

			if (dirty[0] & /*sidebar*/ 256) {
				toggle_class(div3, "bytemd-hidden", /*sidebar*/ ctx[8] === false);
			}

			const status_changes = {};
			if (dirty[0] & /*mergedLocale*/ 2048) status_changes.locale = /*mergedLocale*/ ctx[11];
			if (dirty[0] & /*overridePreview, split*/ 1032) status_changes.showSync = !/*overridePreview*/ ctx[3] && /*split*/ ctx[10];
			if (dirty[0] & /*debouncedValue*/ 32768) status_changes.value = /*debouncedValue*/ ctx[15];
			if (dirty[0] & /*syncEnabled*/ 65536) status_changes.syncEnabled = /*syncEnabled*/ ctx[16];
			if (dirty[0] & /*value, maxLength*/ 17) status_changes.islimited = /*value*/ ctx[0].length > /*maxLength*/ ctx[4];
			status.$set(status_changes);

			if (dirty[0] & /*split, activeTab*/ 1152) {
				toggle_class(div5, "bytemd-split", /*split*/ ctx[10] && /*activeTab*/ ctx[7] === false);
			}

			if (dirty[0] & /*fullscreen*/ 16384) {
				toggle_class(div5, "bytemd-fullscreen", /*fullscreen*/ ctx[14]);
			}
		},
		i(local) {
			if (current) return;
			transition_in(toolbar.$$.fragment, local);
			transition_in(if_block);
			transition_in(help.$$.fragment, local);
			transition_in(toc.$$.fragment, local);
			transition_in(status.$$.fragment, local);
			current = true;
		},
		o(local) {
			transition_out(toolbar.$$.fragment, local);
			transition_out(if_block);
			transition_out(help.$$.fragment, local);
			transition_out(toc.$$.fragment, local);
			transition_out(status.$$.fragment, local);
			current = false;
		},
		d(detaching) {
			if (detaching) detach(div5);
			destroy_component(toolbar);
			/*div0_binding*/ ctx[33](null);
			if (if_block) if_block.d();
			/*div1_binding*/ ctx[35](null);
			destroy_component(help);
			destroy_component(toc);
			destroy_component(status);
			/*div5_binding*/ ctx[40](null);
			mounted = false;
			dispose();
		}
	};
}

function instance($$self, $$props, $$invalidate) {
	let mergedLocale;
	let actions;
	let split;
	let styles;
	let context;
	let { value = '' } = $$props;
	let { plugins = [] } = $$props;
	let { sanitize } = $$props;
	let { mode = 'auto' } = $$props;
	let { previewDebounce = 300 } = $$props;
	let { placeholder } = $$props;
	let { editorConfig } = $$props;
	let { locale } = $$props;
	let { uploadImages } = $$props;
	let { overridePreview } = $$props;
	let { maxLength = Infinity } = $$props;
	const dispatch = createEventDispatcher();
	let root;
	let editorEl;
	let previewEl;
	let containerWidth = Infinity; // TODO: first screen
	let codemirror;
	let editor;
	let activeTab;
	let fullscreen = false;
	let sidebar = false;
	let cbs = [];
	let keyMap = {};

	function on() {
		// console.log('on', plugins);
		cbs = plugins.map(p => {
			var _a;

			return (_a = p.editorEffect) === null || _a === void 0
			? void 0
			: _a.call(p, context);
		});

		keyMap = {};

		// TODO: nested shortcuts
		actions.forEach(({ handler }) => {
			if ((handler === null || handler === void 0
			? void 0
			: handler.type) === 'action' && handler.shortcut) {
				keyMap[handler.shortcut] = () => {
					handler.click(context);
				};
			}
		});

		editor.addKeyMap(keyMap);
	}

	function off() {
		// console.log('off', plugins);
		cbs.forEach(cb => cb && cb());

		editor === null || editor === void 0
		? void 0
		: editor.removeKeyMap(keyMap); // onDestroy runs at SSR, optional chaining here
	}

	let debouncedValue = value;

	const setDebouncedValue = debounce(
		value => {
			$$invalidate(15, debouncedValue = value);

			overridePreview === null || overridePreview === void 0
			? void 0
			: overridePreview(previewEl, { value: debouncedValue, plugins, sanitize });
		},
		previewDebounce
	);

	// Scroll sync vars
	let syncEnabled = true;

	let editCalled = false;
	let previewCalled = false;
	let editPs;
	let previewPs;
	let hast = { type: 'root', children: [] };
	let vfile;
	let currentBlockIndex = 0;

	onMount(async () => {
		$$invalidate(29, codemirror = factory());
		usePlaceholder(codemirror);
		useOverlay(codemirror);
		useXml(codemirror); // inline html highlight
		useMarkdown(codemirror);
		useGfm(codemirror);
		useYaml(codemirror);
		useYamlFrontmatter(codemirror);

		// @ts-ignore TODO: type
		$$invalidate(6, editor = codemirror(editorEl, {
			value,
			mode: 'yaml-frontmatter',
			lineWrapping: true,
			tabSize: 8,
			indentUnit: 4,
			...editorConfig,
			placeholder
		}));

		// https://github.com/codemirror/CodeMirror/issues/2428#issuecomment-39315423
		// https://github.com/codemirror/CodeMirror/issues/988#issuecomment-392232020
		editor.addKeyMap({
			Tab: 'indentMore',
			'Shift-Tab': 'indentLess'
		});

		editor.on('change', () => {
			dispatch('change', { value: editor.getValue() });
		});

		const updateBlockPositions = throttle(
			() => {
				editPs = []; // console.log(editPs, previewPs);
				previewPs = [];
				const scrollInfo = editor.getScrollInfo();
				const body = previewEl.childNodes[0];
				if (!(body instanceof HTMLElement)) return;
				const leftNodes = hast.children.filter(v => v.type === 'element');
				const rightNodes = [...body.childNodes].filter(v => v instanceof HTMLElement);

				for (let i = 0; i < leftNodes.length; i++) {
					const leftNode = leftNodes[i];
					const rightNode = rightNodes[i];

					// if there is no position info, move to the next node
					if (!leftNode.position) {
						continue;
					}

					const left = editor.heightAtLine(leftNode.position.start.line - 1, 'local') / (scrollInfo.height - scrollInfo.clientHeight);
					const right = (rightNode.offsetTop - body.offsetTop) / (previewEl.scrollHeight - previewEl.clientHeight);

					if (left >= 1 || right >= 1) {
						break;
					}

					editPs.push(left);
					previewPs.push(right);
				}

				editPs.push(1);
				previewPs.push(1);
			},
			1000
		); // console.log(editPs, previewPs);

		const editorScrollHandler = () => {
			if (overridePreview) return;
			if (!syncEnabled) return;

			if (previewCalled) {
				previewCalled = false;
				return;
			}

			updateBlockPositions();
			const info = editor.getScrollInfo();
			const leftRatio = info.top / (info.height - info.clientHeight);
			const startIndex = findStartIndex(leftRatio, editPs);
			const rightRatio = (leftRatio - editPs[startIndex]) * (previewPs[startIndex + 1] - previewPs[startIndex]) / (editPs[startIndex + 1] - editPs[startIndex]) + previewPs[startIndex];

			// const rightRatio = rightPs[startIndex]; // for testing
			previewEl.scrollTo(0, rightRatio * (previewEl.scrollHeight - previewEl.clientHeight));

			editCalled = true;
		};

		const previewScrollHandler = () => {
			if (overridePreview) return;

			// find the current block in the view
			updateBlockPositions();

			$$invalidate(19, currentBlockIndex = findStartIndex(previewEl.scrollTop / (previewEl.scrollHeight - previewEl.offsetHeight), previewPs));
			if (!syncEnabled) return;

			if (editCalled) {
				editCalled = false;
				return;
			}

			const rightRatio = previewEl.scrollTop / (previewEl.scrollHeight - previewEl.clientHeight);
			const startIndex = findStartIndex(rightRatio, previewPs);
			const leftRatio = (rightRatio - previewPs[startIndex]) * (editPs[startIndex + 1] - editPs[startIndex]) / (previewPs[startIndex + 1] - previewPs[startIndex]) + editPs[startIndex];
			const info = editor.getScrollInfo();
			editor.scrollTo(0, leftRatio * (info.height - info.clientHeight));
			previewCalled = true;
		};

		editor.on('scroll', editorScrollHandler);
		previewEl.addEventListener('scroll', previewScrollHandler, { passive: true });

		// handle image drop and paste
		const handleImages = async (e, itemList) => {
			if (!uploadImages) return;

			const files = Array.from(itemList !== null && itemList !== void 0 ? itemList : []).map(item => {
				if (item.type.startsWith('image/')) {
					return item.getAsFile();
				}
			}).filter(f => f != null);

			if (files.length) {
				e.preventDefault(); // important
				await handleImageUpload(context, uploadImages, files);
			}
		};

		editor.on('drop', async (_, e) => {
			var _a;

			handleImages(e, (_a = e.dataTransfer) === null || _a === void 0
			? void 0
			: _a.items);
		});

		editor.on('paste', async (_, e) => {
			var _a;

			handleImages(e, (_a = e.clipboardData) === null || _a === void 0
			? void 0
			: _a.items);
		});

		// @ts-ignore
		new ResizeObserver(entries => {
				$$invalidate(28, containerWidth = entries[0].contentRect.width);
			}).observe(root, {
			box: 'border-box', // console.log(containerWidth);
			
		});
	}); // No need to call `on` because cm instance would change once after init

	onDestroy(off);

	const key_handler = e => {
		editor.setOption('keyMap', e.detail);
		editor.focus();
	};

	const tab_handler = e => {
		const v = e.detail;

		if (split) {
			$$invalidate(7, activeTab = activeTab === v ? false : v);
		} else {
			$$invalidate(7, activeTab = v);
		}

		if (activeTab === 'write') {
			tick().then(() => {
				editor && editor.focus();
			});
		}
	};

	const click_handler = e => {
		switch (e.detail) {
			case 'fullscreen':
				$$invalidate(14, fullscreen = !fullscreen);
				break;
			case 'help':
				$$invalidate(8, sidebar = sidebar === 'help' ? false : 'help');
				break;
			case 'toc':
				$$invalidate(8, sidebar = sidebar === 'toc' ? false : 'toc');
				break;
		}
	};

	function div0_binding($$value) {
		binding_callbacks[$$value ? 'unshift' : 'push'](() => {
			editorEl = $$value;
			$$invalidate(12, editorEl);
		});
	}

	const hast_handler = e => {
		$$invalidate(17, hast = e.detail.hast);
		$$invalidate(18, vfile = e.detail.file);
	};

	function div1_binding($$value) {
		binding_callbacks[$$value ? 'unshift' : 'push'](() => {
			previewEl = $$value;
			$$invalidate(13, previewEl);
		});
	}

	const click_handler_1 = () => {
		$$invalidate(8, sidebar = false);
	};

	const click_handler_2 = e => {
		const headings = previewEl.querySelectorAll('h1,h2,h3,h4,h5,h6');
		headings[e.detail].scrollIntoView();
	};

	const sync_handler = e => {
		$$invalidate(16, syncEnabled = e.detail);
	};

	const top_handler = () => {
		editor.scrollTo(null, 0);
		previewEl.scrollTo({ top: 0 });
	};

	function div5_binding($$value) {
		binding_callbacks[$$value ? 'unshift' : 'push'](() => {
			root = $$value;
			$$invalidate(5, root);
		});
	}

	$$self.$$set = $$props => {
		if ('value' in $$props) $$invalidate(0, value = $$props.value);
		if ('plugins' in $$props) $$invalidate(1, plugins = $$props.plugins);
		if ('sanitize' in $$props) $$invalidate(2, sanitize = $$props.sanitize);
		if ('mode' in $$props) $$invalidate(22, mode = $$props.mode);
		if ('previewDebounce' in $$props) $$invalidate(23, previewDebounce = $$props.previewDebounce);
		if ('placeholder' in $$props) $$invalidate(24, placeholder = $$props.placeholder);
		if ('editorConfig' in $$props) $$invalidate(25, editorConfig = $$props.editorConfig);
		if ('locale' in $$props) $$invalidate(26, locale = $$props.locale);
		if ('uploadImages' in $$props) $$invalidate(27, uploadImages = $$props.uploadImages);
		if ('overridePreview' in $$props) $$invalidate(3, overridePreview = $$props.overridePreview);
		if ('maxLength' in $$props) $$invalidate(4, maxLength = $$props.maxLength);
	};

	$$self.$$.update = () => {
		if ($$self.$$.dirty[0] & /*locale*/ 67108864) {
			$$invalidate(11, mergedLocale = { ...en, ...locale });
		}

		if ($$self.$$.dirty[0] & /*mergedLocale, plugins, uploadImages*/ 134219778) {
			$$invalidate(20, actions = getBuiltinActions(mergedLocale, plugins, uploadImages));
		}

		if ($$self.$$.dirty[0] & /*mode, containerWidth*/ 272629760) {
			$$invalidate(10, split = mode === 'split' || mode === 'auto' && containerWidth >= 800);
		}

		if ($$self.$$.dirty[0] & /*split*/ 1024) {
			(_ => {
				// reset active tab
				if (split) $$invalidate(7, activeTab = false);
			})();
		}

		if ($$self.$$.dirty[0] & /*split, activeTab, sidebar*/ 1408) {
			$$invalidate(21, styles = (() => {
				let edit;
				let preview;

				if (split && activeTab === false) {
					if (sidebar) {
						edit = `width:calc(50% - ${sidebar ? 140 : 0}px)`;
						preview = `width:calc(50% - ${sidebar ? 140 : 0}px)`;
					} else {
						edit = 'width:50%';
						preview = 'width:50%';
					}
				} else if (activeTab === 'preview') {
					edit = 'display:none';
					preview = `width:calc(100% - ${sidebar ? 280 : 0}px)`;
				} else {
					edit = `width:calc(100% - ${sidebar ? 280 : 0}px)`;
					preview = 'display:none';
				} // TODO: use width:0 to make scroll sync work until
				// the position calculation improved (causes white screen after switching to editor only)

				return { edit, preview };
			})());
		}

		if ($$self.$$.dirty[0] & /*codemirror, editor, root*/ 536871008) {
			$$invalidate(9, context = (() => {
				const context = {
					// @ts-ignore
					codemirror,
					editor,
					root,
					// @ts-ignore
					...createEditorUtils(codemirror, editor)
				};

				return context;
			})());
		}

		if ($$self.$$.dirty[0] & /*value*/ 1) {
			setDebouncedValue(value);
		}

		if ($$self.$$.dirty[0] & /*editor, value*/ 65) {
			if (editor && value !== editor.getValue()) {
				editor.setValue(value);
			}
		}

		if ($$self.$$.dirty[0] & /*editor, plugins*/ 66) {
			if (editor && plugins) {
				off();

				tick().then(() => {
					on();
				});
			}
		}
	};

	return [
		value,
		plugins,
		sanitize,
		overridePreview,
		maxLength,
		root,
		editor,
		activeTab,
		sidebar,
		context,
		split,
		mergedLocale,
		editorEl,
		previewEl,
		fullscreen,
		debouncedValue,
		syncEnabled,
		hast,
		vfile,
		currentBlockIndex,
		actions,
		styles,
		mode,
		previewDebounce,
		placeholder,
		editorConfig,
		locale,
		uploadImages,
		containerWidth,
		codemirror,
		key_handler,
		tab_handler,
		click_handler,
		div0_binding,
		hast_handler,
		div1_binding,
		click_handler_1,
		click_handler_2,
		sync_handler,
		top_handler,
		div5_binding
	];
}

class Editor extends SvelteComponent {
	constructor(options) {
		super();

		init(
			this,
			options,
			instance,
			create_fragment,
			not_equal,
			{
				value: 0,
				plugins: 1,
				sanitize: 2,
				mode: 22,
				previewDebounce: 23,
				placeholder: 24,
				editorConfig: 25,
				locale: 26,
				uploadImages: 27,
				overridePreview: 3,
				maxLength: 4
			},
			null,
			[-1, -1]
		);
	}
}

export { Editor, Viewer, getProcessor };
//# sourceMappingURL=index.esm.js.map
